import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, Link } from 'react-router';
import { api, ApiError } from '../api/client';
import {
    Button, Card, Field, TextInput, TextArea, Checkbox, Loading, ErrorNote, toast
} from '../components/ui';

interface RuleRow {
    eventType: string;
    label: string;
    explanation: string;
    usesThreshold: boolean;
    usesSustained: boolean;
    isEnabled: boolean;
    severity: number;
    deduction: number;
    categoryCap: number;
    sustainedSeconds: number;
    thresholdValue: number | null;
}

interface Profile {
    id?: number;
    name: string;
    description: string | null;
    isDefault: boolean;
    recordVideo: boolean;
    recordingBitrateKbps: number;
    snapshotIntervalSeconds: number;
    captureSnapshotOnViolation: boolean;
    snapshotThrottleSeconds: number;
    requireFullscreen: boolean;
    requireSingleFace: boolean;
    requireMicrophone: boolean;
    minLightingLuma: number;
    requireSingleScreen: boolean;
    maskOnFocusLoss: boolean;
    blockKeyboardInput: boolean;
    requireDeviceRehearsal: boolean;
    autoSubmitAfterAwaySeconds: number;
    warnCandidateOnViolation: boolean;
    pauseOnCameraProblem: boolean;
    serverVerifySnapshots: boolean;
    detectEmotion: boolean;
    cleanThreshold: number;
    minorThreshold: number;
    suspiciousThreshold: number;
    rules: RuleRow[];
}

/**
 * Every toggle carries help text that says what the check actually does — and, where it matters,
 * what it cannot do. Overstating proctoring is how a recruiter ends up trusting a signal that was
 * never trustworthy.
 */
const TOGGLES: { key: keyof Profile; label: string; hint: string }[] = [
    { key: 'requireSingleFace', label: 'Require the camera, and one face in it', hint: 'Without a camera there is nothing to proctor, so this also makes the camera mandatory.' },
    { key: 'requireMicrophone', label: 'Require a working microphone', hint: 'Answers are spoken, so an interview without one records nothing.' },
    { key: 'requireFullscreen', label: 'Require fullscreen', hint: 'Leaving fullscreen is recorded as a signal while this is on.' },
    { key: 'recordVideo', label: 'Record the session', hint: 'Uploaded in chunks during the interview, so a crash does not lose all of it.' },
    { key: 'requireDeviceRehearsal', label: 'Check camera, microphone and speakers before starting', hint: 'The candidate plays a test tone, speaks, and confirms their picture before Start unlocks. Catches a broken device before it costs them the interview.' },
    { key: 'blockKeyboardInput', label: 'Lock the keyboard during the assessment', hint: 'Answers are spoken, so typing has no purpose. This blocks key presses inside the page and records them — it cannot stop operating-system shortcuts, and it is not a substitute for watching the recording.' },
    { key: 'warnCandidateOnViolation', label: 'Warn the candidate when a camera rule trips', hint: 'Off means the interview stays silent and the recruiter finds out afterwards.' },
    { key: 'pauseOnCameraProblem', label: 'Pause the interview until a camera problem is fixed', hint: 'Needs the warning above. Nothing is ever terminated and no answer is discarded — the clock stops and resumes.' },
    { key: 'requireSingleScreen', label: 'Check for a second screen', hint: 'A browser cannot detect screen sharing — no software running in a page can. It can see an extra display, which is somewhere answers could be read from.' },
    { key: 'maskOnFocusLoss', label: 'Blur the question when the window is not in front', hint: 'A deterrent only. A web page cannot prevent a screenshot, and a phone camera defeats any software check.' },
    { key: 'captureSnapshotOnViolation', label: 'Capture a picture when a signal fires', hint: 'The frames a reviewer actually needs as evidence.' },
    { key: 'serverVerifySnapshots', label: 'Re-check those pictures on the server', hint: 'Counts faces again here, which is what turns "the browser said so" into something defensible.' },
    { key: 'detectEmotion', label: 'Detect expression in those pictures', hint: 'Recorded as context on the timeline. It never affects the integrity score.' }
];

export function ProctoringEdit() {
    const { id } = useParams();
    const isNew = id === 'new';
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [profile, setProfile] = useState<Profile | null>(null);
    const [error, setError] = useState<ApiError | null>(null);
    const [busy, setBusy] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ['proctoring', id],
        queryFn: () => api.get<Profile>(`/api/admin/proctoring/${id}`),
        enabled: !isNew
    });

    // A new profile borrows the default's shape, including every signal row, so the form is never
    // built from guesses about what exists.
    const seed = useQuery({
        queryKey: ['proctoring-seed'],
        queryFn: async () => {
            const list = await api.get<{ id: number; isDefault: boolean }[]>('/api/admin/proctoring');
            const template = list.find((p) => p.isDefault) ?? list[0];
            return template ? api.get<Profile>(`/api/admin/proctoring/${template.id}`) : null;
        },
        enabled: isNew
    });

    useEffect(() => {
        const source = isNew ? seed.data : data;

        if (source) {
            setProfile(isNew
                ? { ...source, id: undefined, name: '', description: '', isDefault: false }
                : source);
        }
    }, [data, seed.data, isNew]);

    const setField = <K extends keyof Profile>(key: K, value: Profile[K]) =>
        setProfile((p) => (p ? { ...p, [key]: value } : p));

    const setRule = (eventType: string, patch: Partial<RuleRow>) =>
        setProfile((p) => p ? {
            ...p,
            rules: p.rules.map((r) => r.eventType === eventType ? { ...r, ...patch } : r)
        } : p);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!profile) return;

        setBusy(true);
        setError(null);

        try {
            await api.post('/api/admin/proctoring', profile);
            await queryClient.invalidateQueries({ queryKey: ['proctoring'] });
            toast('Profile saved.');
            navigate('/proctoring');
        } catch (caught) {
            setError(caught as ApiError);
        } finally {
            setBusy(false);
        }
    };

    if (isLoading || seed.isLoading || !profile) return <Loading />;

    return (
        <form onSubmit={submit} className="space-y-6">
            {error && !error.errors && <ErrorNote message={error.message} />}

            <Card heading title={isNew ? 'Add a proctoring profile' : `Edit ${data?.name}`}>
                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Name" error={error?.fieldError('name')}>
                        <TextInput
                            value={profile.name}
                            onChange={(e) => setField('name', e.target.value)}
                            invalid={!!error?.fieldError('name')}
                        />
                    </Field>

                    <Field label="Description">
                        <TextInput
                            value={profile.description ?? ''}
                            onChange={(e) => setField('description', e.target.value)}
                        />
                    </Field>
                </div>

                <div className="mt-4">
                    <Checkbox
                        label="Use this profile by default"
                        checked={profile.isDefault}
                        onChange={(checked) => setField('isDefault', checked)}
                    />
                </div>
            </Card>

            <Card title="What is checked">
                <div className="grid gap-3 sm:grid-cols-2">
                    {TOGGLES.map((toggle) => (
                        <Checkbox
                            key={String(toggle.key)}
                            label={toggle.label}
                            hint={toggle.hint}
                            checked={profile[toggle.key] as boolean}
                            onChange={(checked) => setField(toggle.key, checked as never)}
                        />
                    ))}
                </div>
            </Card>

            <Card title="Numbers">
                <div className="grid gap-4 sm:grid-cols-3">
                    <Field label="Recording quality" hint="Kilobits per second.">
                        <TextInput
                            type="number" min="100"
                            value={profile.recordingBitrateKbps}
                            onChange={(e) => setField('recordingBitrateKbps', Number(e.target.value))}
                        />
                    </Field>

                    <Field label="Picture every" hint="Seconds. 0 switches routine pictures off.">
                        <TextInput
                            type="number" min="0"
                            value={profile.snapshotIntervalSeconds}
                            onChange={(e) => setField('snapshotIntervalSeconds', Number(e.target.value))}
                        />
                    </Field>

                    <Field label="At most one picture every" hint="Seconds, so a burst of signals does not fill storage.">
                        <TextInput
                            type="number" min="0"
                            value={profile.snapshotThrottleSeconds}
                            onChange={(e) => setField('snapshotThrottleSeconds', Number(e.target.value))}
                        />
                    </Field>

                    <Field
                        label="Submit automatically after away for"
                        hint="Seconds the candidate may spend off the page or out of fullscreen before what they have said is submitted for them. They are warned at the half-way point. 0 switches this off."
                    >
                        <TextInput
                            type="number" min="0" max="300"
                            value={profile.autoSubmitAfterAwaySeconds}
                            onChange={(e) => setField('autoSubmitAfterAwaySeconds', Number(e.target.value))}
                        />
                    </Field>

                    <Field label="Minimum room brightness" hint="0 to 255. Checked before the interview starts.">
                        <TextInput
                            type="number" min="0" max="255"
                            value={profile.minLightingLuma}
                            onChange={(e) => setField('minLightingLuma', Number(e.target.value))}
                        />
                    </Field>
                </div>

                <h3 className="mt-6 mb-2 text-sm font-semibold text-slate-700">Verdict bands</h3>
                <p className="mb-3 text-xs text-slate-500">
                    A score at or above each number gets that verdict. They must descend.
                </p>

                <div className="grid gap-4 sm:grid-cols-3">
                    <Field label="Clean at or above" error={error?.fieldError('cleanThreshold')}>
                        <TextInput
                            type="number" min="0" max="100"
                            value={profile.cleanThreshold}
                            onChange={(e) => setField('cleanThreshold', Number(e.target.value))}
                            invalid={!!error?.fieldError('cleanThreshold')}
                        />
                    </Field>

                    <Field label="Minor concerns at or above">
                        <TextInput
                            type="number" min="0" max="100"
                            value={profile.minorThreshold}
                            onChange={(e) => setField('minorThreshold', Number(e.target.value))}
                        />
                    </Field>

                    <Field label="Suspicious at or above">
                        <TextInput
                            type="number" min="0" max="100"
                            value={profile.suspiciousThreshold}
                            onChange={(e) => setField('suspiciousThreshold', Number(e.target.value))}
                        />
                    </Field>
                </div>
            </Card>

            <Card title="Signals and what they cost">
                <p className="mb-4 text-sm text-slate-600">
                    Every interview starts at 100. A signal deducts each time it fires, up to its cap.
                    A cap of 0 means no cap.
                </p>

                <div className="space-y-3">
                    {profile.rules.map((rule) => (
                        <div
                            key={rule.eventType}
                            className="rounded-lg border border-slate-200 p-3.5"
                        >
                            <Checkbox
                                label={rule.label}
                                hint={rule.explanation}
                                checked={rule.isEnabled}
                                onChange={(checked) => setRule(rule.eventType, { isEnabled: checked })}
                            />

                            {rule.isEnabled && (
                                <div className="mt-3 grid gap-3 pl-6 sm:grid-cols-4">
                                    <Field label="Deducts">
                                        <TextInput
                                            type="number" min="0" max="100"
                                            value={rule.deduction}
                                            onChange={(e) =>
                                                setRule(rule.eventType, { deduction: Number(e.target.value) })}
                                        />
                                    </Field>

                                    <Field label="Up to">
                                        <TextInput
                                            type="number" min="0" max="100"
                                            value={rule.categoryCap}
                                            onChange={(e) =>
                                                setRule(rule.eventType, { categoryCap: Number(e.target.value) })}
                                        />
                                    </Field>

                                    <Field label="Seriousness" hint="0 is a note only.">
                                        <TextInput
                                            type="number" min="0" max="3"
                                            value={rule.severity}
                                            onChange={(e) =>
                                                setRule(rule.eventType, { severity: Number(e.target.value) })}
                                        />
                                    </Field>

                                    {rule.usesSustained && (
                                        <Field label="After how long" hint="Seconds it must hold.">
                                            <TextInput
                                                type="number" min="0"
                                                value={rule.sustainedSeconds}
                                                onChange={(e) =>
                                                    setRule(rule.eventType, { sustainedSeconds: Number(e.target.value) })}
                                            />
                                        </Field>
                                    )}

                                    {rule.usesThreshold && (
                                        <Field label="Tolerance" hint="Degrees away from the screen.">
                                            <TextInput
                                                type="number" min="0"
                                                value={rule.thresholdValue ?? 0}
                                                onChange={(e) =>
                                                    setRule(rule.eventType, { thresholdValue: Number(e.target.value) })}
                                            />
                                        </Field>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </Card>

            <div className="flex gap-2">
                <Button type="submit" busy={busy}>Save profile</Button>
                <Link to="/proctoring">
                    <Button type="button" variant="secondary">Cancel</Button>
                </Link>
            </div>
        </form>
    );
}
